import { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Button from "components/Buttons/Button/Button";
import InputField from "components/InputField/InputField";
import Loading from "components/Loading/Loading";
import useSnackbar from "hooks/useSnackbar";
import OrderService from "services/OrderService";
import ButtonCloseModal from "components/Buttons/ButtonCloseModal/ButtonCloseModal";
import { Color } from "enums/Common";
import { OrderVoidAction } from "enums/Order";
import { IOrderCancelOptions } from "interfaces/Order";
import { getVoidChargeLines } from "helpers/OrderHelper";

interface CancelOrderModalProps {
  orderId: string;
  onClose: () => void;
  onSuccess: () => void;
}

enum CancelOption {
  Cancel = "cancel",
  Void = "void",
  RequestVoid = "request",
}

interface IOptionConfig {
  value: CancelOption;
  title: string;
  description: string;
  note: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  actionLabel: string;
}

const optionConfigs: Record<CancelOption, IOptionConfig> = {
  [CancelOption.Cancel]: {
    value: CancelOption.Cancel,
    title: "Cancel Order",
    description: "End an active Remote Monitoring order.",
    note: "This will be the final billing cycle.",
    reasonLabel: "Reason for Cancellation",
    reasonPlaceholder: "Please enter the reason for canceling this order.",
    actionLabel: "Cancel Order",
  },
  [CancelOption.Void]: {
    value: CancelOption.Void,
    title: "Void Order",
    description: "Remove an order created in error.",
    note: "No further billing will occur.",
    reasonLabel: "Reason for Voiding",
    reasonPlaceholder: "Please enter the reason for voiding this order.",
    actionLabel: "Void Order",
  },
  [CancelOption.RequestVoid]: {
    value: CancelOption.RequestVoid,
    title: "Request to Void Order",
    description: "Ask BioMech to void this order and review past charges.",
    note: "Orders already shipped or billed require BioMech approval.",
    reasonLabel: "Reason for Void Request",
    reasonPlaceholder: "Please enter the reason you are requesting to void this order.",
    actionLabel: "Submit Void Request",
  },
};

const CancelOrderModal = (props: CancelOrderModalProps) => {
  const { orderId, onClose, onSuccess } = props;

  const { snackbarComponent, setSnackbarError } = useSnackbar();

  const [options, setOptions] = useState<IOrderCancelOptions>();
  const [selected, setSelected] = useState<CancelOption>();
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [completed, setCompleted] = useState<CancelOption>();

  useEffect(() => {
    OrderService.getCancelOptions(orderId)
      .then(({ data }) => setOptions(data))
      .catch(() => setSnackbarError())
      .finally(() => setLoading(false));
  }, [orderId, setSnackbarError]);

  const availableOptions: IOptionConfig[] = [
    optionConfigs[CancelOption.Cancel],
    ...(options?.voidAction === OrderVoidAction.Void
      ? [optionConfigs[CancelOption.Void]]
      : options?.voidAction === OrderVoidAction.Request
      ? [optionConfigs[CancelOption.RequestVoid]]
      : []),
  ];

  const selectedConfig = selected ? optionConfigs[selected] : undefined;

  const handleSubmit = () => {
    if (!selected || !notes.trim()) return;

    const data = { orderId, comment: notes.trim() };
    const request =
      selected === CancelOption.Void
        ? OrderService.void(data)
        : selected === CancelOption.RequestVoid
        ? OrderService.requestVoid(data)
        : OrderService.cancel(data);

    setLoading(true);
    request
      .then(() => {
        onSuccess();
        if (selected === CancelOption.Cancel) onClose();
        else setCompleted(selected);
      })
      .catch(() => setSnackbarError())
      .finally(() => setLoading(false));
  };

  // admin voiding an order that was already processed: show what happens to billing
  const showProcessedWarning =
    selected === CancelOption.Void && options?.isProcessed && options.info;

  const renderCompleted = () => (
    <div className="flex flex-col pt-1">
      {completed === CancelOption.Void ? (
        <>
          <h1 className="text-cloudBurst text-lg font-bold mb-2">
            Order voided
          </h1>
          <p className="text-sm">
            The order has been voided. No further billing will occur for this order.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-cloudBurst text-lg font-bold mb-2">
            Void request submitted
          </h1>
          <p className="text-sm">
            The order has been sent to BioMech for review.
          </p>
        </>
      )}

      <Button className="mt-5 self-end !bg-pelorous" onClick={onClose}>
        OK
      </Button>
    </div>
  );

  return (
    <Modal open onClose={onClose}>
      <>
        <div
          className={`absolute w-4/5 ${completed ? "max-w-[420px]" : "max-w-[660px]"} flex flex-col top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md shadow-2xl bg-white px-8 py-7`}
        >
          <ButtonCloseModal
            className={completed ? "!top-5" : "!top-5 left-4"}
            color={Color.CloudBurst}
            onClick={onClose}
          />

          {completed ? (
            renderCompleted()
          ) : (
            <>
              <h1 className="text-center text-cloudBurst text-lg leading-5 font-bold">
                Order Cancellation Options
              </h1>
              <p className="text-center mt-1 mb-5 text-sm">
                Select the reason for this cancellation.
              </p>

              <div className="flex flex-col gap-3">
                {availableOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-4 rounded-lg border px-4 py-3 cursor-pointer ${
                      selected === option.value
                        ? "border-malibu bg-pattensBlue4/40"
                        : "border-pattensBlue2"
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancel-option"
                      className="w-5 h-5 shrink-0 accent-pelorous"
                      checked={selected === option.value}
                      onChange={() => setSelected(option.value)}
                    />
                    <span className="flex flex-col">
                      <b className="text-cloudBurst">{option.title}</b>
                      <span className="text-sm">
                        {option.description}{" "}
                        <span className="text-fiord/70">{option.note}</span>
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              {options?.voidRequested && (
                <p className="mt-3 text-sm text-fiord">
                  A void request for this order is pending BioMech review.
                </p>
              )}

              {showProcessedWarning && options?.info && (
                <div className="mt-3 flex gap-2 rounded-md border border-tahitiGold/50 bg-tahitiGold/10 px-3 py-2 text-sm">
                  <ErrorOutlineIcon className="text-tahitiGold" />
                  <p>
                    <b>This order has already been processed.</b>{" "}
                    {getVoidChargeLines(options.info)
                      .map((line) => `${line}.`)
                      .join(" ")}
                  </p>
                </div>
              )}

              {selectedConfig && (
                <>
                  <div className="mt-4">
                    <InputField
                      required
                      multiline
                      rows={3}
                      label={selectedConfig.reasonLabel}
                      name="notes"
                      placeholder={selectedConfig.reasonPlaceholder}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>

                  <div className="flex gap-5 justify-center mt-6">
                    <Button
                      className="border-2 border-pelorous !bg-white !text-pelorous"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>

                    <Button
                      className="!bg-crimson"
                      disabled={!notes.trim()}
                      onClick={handleSubmit}
                    >
                      {selectedConfig.actionLabel}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

          <Loading loading={loading} />
        </div>

        {snackbarComponent}
      </>
    </Modal>
  );
};

export default CancelOrderModal;
